/**
 * Clipboard access.
 *
 * `navigator.clipboard` is the modern API but it is unavailable outside a secure
 * context, which includes any deployment reached over plain HTTP on a local
 * network — exactly how a merchant may be using the dashboard on a shop iPad.
 * The deprecated `execCommand` path covers that, so the control works wherever
 * the app runs rather than silently failing on the deployment that needs it most.
 */

/**
 * Copies text to the clipboard.
 *
 * @param value The text to copy.
 * @returns True when the copy succeeded.
 */
export async function copyText(value: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Permission denied or an insecure context; fall through to the legacy path.
    }
  }

  if (typeof document === 'undefined') return false;

  const field = document.createElement('textarea');
  field.value = value;
  // Off-screen rather than `display: none`, because a hidden element cannot be
  // selected and the selection is what `execCommand` copies.
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '-1000px';
  field.style.opacity = '0';

  document.body.appendChild(field);

  try {
    field.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(field);
  }
}
